import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebouncedValue } from '@mantine/hooks';
import { shouldValidDirtyTouch } from 'utility/functions';
import { FullEditorialAsset } from 'utility/types/dataProvider';
import { isPlainObject } from 'remeda';

type CustomKV = {
  key: string;
  value: string;
  originalKey: string;
};

type ErrorMap = Record<number, { key?: string; value?: string }>;

type AssetProperties = FullEditorialAsset['properties'];

/**
 * CMSADMIN-1336
 *
 * removes any 'custom_' prefixed keys in properties.
 * to prevent response setting back when posting to api
 *
 * note: also used at the AssetConfigContextProvider
 * level in formatAssetFormDefaultValues for setup form DVs
 * but since we moved to top level 'custom' for custom props
 * field, this may not be neccesary soon as API may handle
 * deletion for us automatically in the future anyways
 */
export function cleanCustomProperties(properties: AssetProperties): AssetProperties {
  // empty if falsy or not an object
  if (!properties || !isPlainObject(properties)) {
    return {};
  }
  // filter out keys that start with 'custom_'
  const cleaned = Object.fromEntries(
    Object.entries(properties).filter(([k]) => !k.startsWith('custom_')),
  );
  return cleaned;
}

export function useCustomProperties(initial: CustomKV[]) {
  const { t } = useTranslation();
  const [customPropsList, setCustomPropsList] = useState<CustomKV[]>(initial);
  const [errors, setErrors] = useState<ErrorMap>({});
  const [debouncedList] = useDebouncedValue(customPropsList, 300);
  const { setValue, getValues } = useFormContext();

  const validate = useCallback(
    (list: CustomKV[]): ErrorMap => {
      const err: ErrorMap = {};
      const usedKeys = new Set<string>();

      list.forEach(({ key }, i) => {
        const trimmed = key.trim();
        const entryError: { key?: string } = {};
        if (!trimmed)
          entryError.key = t('blox.editorial.asset.properties.custom.name.help.required');
        else if (/\s/.test(trimmed))
          entryError.key = t('blox.editorial.asset.properties.custom.name.help.spaces');
        else if (usedKeys.has(trimmed))
          entryError.key = t('blox.editorial.asset.properties.custom.name.help.unique');
        usedKeys.add(trimmed);
        if (Object.keys(entryError).length) err[i] = entryError;
      });

      return err;
    },
    [t],
  );

  const syncToForm = useCallback(
    (list: CustomKV[]) => {
      const result: Record<string, string> = {};
      list.forEach(({ key, value }) => {
        if (key.trim()) result[key.trim()] = value;
      });
      setValue('custom', result, shouldValidDirtyTouch);
    },
    [setValue],
  );

  const handleChange = (index: number, field: 'key' | 'value', value: string) => {
    const copy = [...customPropsList];
    copy[index] = { ...copy[index], [field]: value };
    setCustomPropsList(copy);
  };

  const handleRemove = (index: number) => {
    const copy = [...customPropsList];
    const removed = copy.splice(index, 1)[0];
    const current = getValues('custom') || {};
    if (removed.originalKey in current) delete current[removed.originalKey];
    setCustomPropsList(copy);
    setValue('custom', current, shouldValidDirtyTouch);
  };

  const handleAdd = () => {
    setCustomPropsList([...customPropsList, { key: '', value: '', originalKey: '' }]);
  };

  return {
    customPropsList,
    setCustomPropsList,
    debouncedList,
    errors,
    setErrors,
    validate,
    handleChange,
    handleAdd,
    handleRemove,
    syncToForm,
  };
}
